/**
 * What each LLM-backed canvas feature costs, and the label its `credit_history`
 * row is filed under.
 *
 * Everything here is prefixed `CANVAS_`/`Canvas` on purpose: `accounts` and
 * `credit_history` live in the shared `jodb` database, written by the yo agent
 * and the Figma plugin as well as by this app, so a row saying `PersonaTest`
 * would be ambiguous about which product spent the credits. The name says the
 * source.
 *
 * Prices mirror
 * `floto-agent-os/agents/design-qa/src/common/constants/credit.constants.ts`
 * wherever a feature has an analogue there — asking a panel is that repo's
 * PERSONA_TEST at 50 — so a user reading
 * one `credit_history` list sees consistent pricing across both products. The two with no analogue (walking a captured
 * flow, driving a real browser) are ours.
 *
 * Naming a capture and describing a flow are deliberately absent: they fire on
 * every capture without anyone asking for them, and a 0-credit debit row per
 * screenshot is noise in the list a user reads to understand their bill.
 *
 * Making personas — a panel, or one described by hand — is free too: it is the
 * setup a room needs, not the work a user pays for. Their task types stay
 * below so rows already in `credit_history` keep their labels.
 */
export const CANVAS_CREDITS = {
  /** One persona driving the real product in a live browser. */
  LIVE_TEST: 100,
  /** Asking the panel about one screen. */
  PERSONA_FEEDBACK: 50,
  /** Writing a lookalike population's sampling schema from a seed persona —
   *  one structured-output call. What
   *  it unlocks (100-1000 sampled lookalikes, re-sampled free on every weight
   *  change) costs nothing further: sampling is seeded-PRNG code, not model
   *  calls. */
  PERSONA_LOOKALIKES: 10,
  /** A finished session's report as a presentation deck. Flat, and charged
   *  once per room: downloading it again, or rebuilding it, is free. */
  RESEARCH_DECK: 50,
  /** One persona walking a flow of captured screens. */
  TASK_TEST: 20,
} as const;

export const CANVAS_TASK_TYPES = {
  LIVE_TEST: 'CanvasLiveTest',
  PERSONA_CUSTOM: 'CanvasPersonaCustom',
  PERSONA_FEEDBACK: 'CanvasPersonaFeedback',
  PERSONA_LOOKALIKES: 'CanvasPersonaLookalikes',
  PERSONA_PANEL: 'CanvasPersonaPanel',
  /** One question or poll put to a research room. Priced from its tokens
   *  rather than from `CANVAS_CREDITS` — see `apps/api/src/credits/pricing.ts`. */
  RESEARCH_ROUND: 'CanvasResearchRound',
  /** One update of a room's write-up on the board, priced from its tokens
   *  like a round. */
  RESEARCH_ANALYSIS: 'CanvasResearchAnalysis',
  RESEARCH_DECK: 'CanvasResearchDeck',
  TASK_TEST: 'CanvasTaskTest',
} as const;

export type CanvasTaskType =
  (typeof CANVAS_TASK_TYPES)[keyof typeof CANVAS_TASK_TYPES];

/**
 * A research round has no fixed price — it is charged what its tokens cost
 * (`apps/api/src/credits/pricing.ts`). What is shared is the *estimate* the
 * balance is checked against before a question runs, so the composer's
 * "questions left" and the server's refusal agree.
 */
export const RESEARCH_ROUND_CREDITS = {
  /** What the first question in a room is checked against, before the room
   *  has a round of its own to go by. */
  FIRST_ESTIMATE: 10,
  /** A guard against a runaway round, not a price. */
  SAFETY_CAP: 50,
} as const;

/** What a room's next round is likely to cost: its last round, with headroom
 *  for a transcript that has grown since. A check, not a quote. */
export function estimateResearchRoundCredits(
  lastRoundCredits?: number,
): number {
  if (!lastRoundCredits) return RESEARCH_ROUND_CREDITS.FIRST_ESTIMATE;
  return Math.min(
    RESEARCH_ROUND_CREDITS.SAFETY_CAP,
    Math.ceil(lastRoundCredits * 1.25),
  );
}

/**
 * A write-up update is priced from its tokens too; what is shared is the
 * estimate shown on its button and checked before it runs.
 */
export const RESEARCH_ANALYSIS_CREDITS = {
  /** Before a room has been written up once. */
  FIRST_ESTIMATE: 3,
} as const;

/** What the room's next write-up is likely to cost: its last, with headroom
 *  for a transcript that has grown since. */
export function estimateResearchAnalysisCredits(lastCredits?: number): number {
  if (!lastCredits) return RESEARCH_ANALYSIS_CREDITS.FIRST_ESTIMATE;
  return Math.min(
    RESEARCH_ROUND_CREDITS.SAFETY_CAP,
    Math.ceil(lastCredits * 1.25),
  );
}
